import type { BannerSeverity } from '@@/api/api-outage-notifications';
import { AlertTriangle } from 'lucide-react';

type Brand = 'portal' | 'central';

function getSeverityClassName(severity: BannerSeverity, brand: Brand): string {
  if (severity === 'low') {
    // Brand-specific low severity colors
    return brand === 'portal' ? 'bg-cxportal-blue text-white' : 'bg-cxcentral-green text-white';
  }

  const severityClasses: Record<Exclude<BannerSeverity, 'low'>, string> = {
    moderate: 'bg-[#faad14] text-black',
    high: 'bg-[#f5222d] text-white',
    critical: 'bg-[#8B0000] text-white'
  };

  return severityClasses[severity] || 'bg-[#faad14] text-black';
}

function formatUpdatedAt(updatedAt?: string) {
  if (!updatedAt) return null;
  try {
    const d = new Date(updatedAt);
    const timeET = d
      .toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York'
      })
      .toLowerCase();
    return `Updated ${timeET} EST`;
  } catch {
    return null;
  }
}

export function BannerAlert({
  message,
  severity,
  updatedAt,
  brand = 'central'
}: {
  message: string;
  severity: BannerSeverity;
  updatedAt?: string;
  brand?: Brand;
}) {
  const severityClassName = getSeverityClassName(severity, brand);
  const updated = formatUpdatedAt(updatedAt);
  const showWarningIcon = severity === 'high' || severity === 'critical';
  return (
    <div className={`w-full text-center px-4 py-2 ${severityClassName}`} role='status' aria-live='polite'>
      {updated ? (
        <span className='inline-flex items-center gap-2'>
          {showWarningIcon ? <AlertTriangle className='h-8 w-8' aria-hidden /> : null}
          <span className='text-sm'>
            <span className='font-medium'>{updated}</span>: {message}
          </span>
        </span>
      ) : (
        <span className='inline-flex items-center gap-2 text-sm'>
          {showWarningIcon ? <AlertTriangle className='h-8 w-8' aria-hidden /> : null}
          {message}
        </span>
      )}
    </div>
  );
}
